import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';

import { GenericFormType } from 'app/core/enums/generated/GenericFormType';
import { SystemMessage } from 'app/core/system-message/system-message-service';
import { NotificationService } from 'app/notification.service';
import { RecaptchaService } from 'app/services/recaptcha.service';
import { MailToFacilityVm } from 'app/shared/generated/HelpDesk/Models/MailToFacilityVm';
import { MedicationAssistanceFormController } from 'app/shared/medication-assistance/medication-assistance-form.controller';
import { PUBLIC_SMARTY_COUNTY_API } from 'app/shared/medication-assistance/medication-assistance.constants';
import { AcknowledgmentSectionComponent } from 'app/shared/medication-assistance/sections/acknowledgment-section.component';
import { ApplicantInfoSectionComponent } from 'app/shared/medication-assistance/sections/applicant-info-section.component';
import { HealthConditionsSectionComponent } from 'app/shared/medication-assistance/sections/health-conditions-section.component';
import { MedicalInfoSectionComponent } from 'app/shared/medication-assistance/sections/medical-info-section.component';
import { PrimeNgModule } from 'app/shared/primeNg.module';

interface PublicStep {
    key: string;
    label: string;
    group: string;
    /** Optional steps stay valid when empty; they only show complete once the applicant moves past. */
    optional?: boolean;
}

/**
 * The anonymous Insulin Distribution Program application.
 *
 * The four steps are the same components the authenticated help-desk form uses, so the questions an
 * applicant answers here are the questions CHD staff see, asked identically. What this page adds is
 * everything that follows from there being no signed-in user: branding and an explanation of the
 * programme, reCAPTCHA, a honeypot, an idempotency key that survives a reload, and a confirmation
 * screen. The signature is the same drawn one every other path captures.
 *
 * There is deliberately no "save and finish later", and no way to look an application up afterwards.
 * A confirmation number is a reference for a phone call, not a credential, and a page that accepted
 * one and returned an application would hand anybody who guessed a number somebody else's PHI.
 */
@Component({
    selector: 'pcg-insulin-application'
    , changeDetection: ChangeDetectionStrategy.OnPush
    , imports: [
        ReactiveFormsModule
        , PrimeNgModule
        , ApplicantInfoSectionComponent
        , MedicalInfoSectionComponent
        , HealthConditionsSectionComponent
        , AcknowledgmentSectionComponent
    ]
    , providers: [ MedicationAssistanceFormController ]
    , templateUrl: './insulin-application.component.html'
    , styleUrl: './insulin-application.component.scss'
})
export class InsulinApplicationComponent implements OnInit, OnDestroy {

    private fb = inject(FormBuilder);
    private http = inject(HttpClient);
    private ns = inject(NotificationService);
    private host = inject(ElementRef<HTMLElement>);
    private recaptchaService = inject(RecaptchaService);

    maForm = inject(MedicationAssistanceFormController);

    /** Where the applicant is in the flow, and what they see once they are through it. */
    currentStep = signal<string>('applicant');
    submitted = signal<boolean>(false);
    confirmationNumber = signal<string>(null);
    facilityName = signal<string>(null);
    isSubmitting = signal<boolean>(false);

    /**
     * Whether Submit has been pressed and nothing has been edited since. The outstanding-work summary
     * is gated on this, so it answers an attempt to submit rather than watching the applicant type.
     *
     * Set by <see cref="submit"/> and cleared by the first edit afterwards - see the valueChanges
     * subscription in buildForm for why both halves are needed. Telling somebody their work is
     * unfinished while they are visibly in the middle of finishing it is noise; the summary is a list
     * of links back to what was missed, and that only means anything just after something was missed.
     *
     * startAnother() reloads the page, so a fresh application gets a fresh component and no reset
     * beyond this is needed.
     */
    submitAttempted = signal<boolean>(false);

    /**
     * The drawn signature, as base64 PNG without the data-URI prefix.
     *
     * Held outside the form because the pad produces an image rather than a value a control
     * would hold, and mirrored onto the posted signatureBase64 field on every change.
     */
    signature = signal<string>(null);
    stepState = signal<Record<string, boolean>>({});
    visitedSteps = signal<Set<string>>(new Set<string>());

    form!: FormGroup;

    readonly countyApi = PUBLIC_SMARTY_COUNTY_API;

    /**
     * False restricts the address lookup to Florida. The programme is Florida-only and the application
     * asks the applicant to confirm they live here, so offering out-of-state addresses would only
     * invite one that cannot qualify.
     */
    readonly showAllStates = false;

    readonly steps: PublicStep[] = [
        { key: 'applicant', label: 'Applicant Info', group: 'applicantInfo' }
        , { key: 'medical', label: 'Medical Info', group: 'medicalInfo' }
        , { key: 'conditions', label: 'Health Conditions', group: 'healthConditions', optional: true }
        , { key: 'acknowledgment', label: 'Acknowledgment & Signature', group: 'acknowledgment' }
    ];

    /** Every active CHD. Not narrowed: an applicant may deal with one outside where they live. */
    servicingFacilities = signal<MailToFacilityVm[]>([]);

    /**
     * A CHD's address on one line, "1 Main St, Tallahassee, FL 32303", shown under its name in the
     * dropdown so an applicant can tell which office they are choosing (DEPCS-4963). Empty parts are
     * dropped rather than left as stray commas.
     */
    facilityAddress(f: MailToFacilityVm): string {
        const stateZip = [f?.state, f?.zip].filter(o => !!o).join(' ');
        return [f?.address, f?.city, stateZip].filter(o => !!o && `${o}`.trim()).join(', ');
    }

    currentIndex = computed(() => this.steps.findIndex(s => s.key === this.currentStep()));
    isFirstStep = computed(() => this.currentIndex() === 0);
    isLastStep = computed(() => this.currentIndex() === this.steps.length - 1);
    firstInvalidIndex = computed(() => {
        const st = this.stepState();
        return this.steps.findIndex(s => !st[s.key]);
    });

    /** Guardian details are asked for only when the applicant is a minor on the day they sign. */
    get isMinor(): boolean { return this.maForm.isMinor(); }
    get dataGroup(): FormGroup { return this.form.get('data') as FormGroup; }
    get applicant(): FormGroup { return this.maForm.applicant; }
    get medical(): FormGroup { return this.maForm.medical; }
    get conditions(): FormGroup { return this.maForm.conditions; }
    get acknowledgment(): FormGroup { return this.maForm.acknowledgment; }

    constructor(title: Title) { title.setTitle('Insulin Distribution Program Application'); }

    ngOnInit(): void {
        this.buildForm();
        this.loadFacilities();
    }

    ngOnDestroy(): void { this.maForm.destroy(); }

    onSignatureChange(value: string): void {
        this.signature.set(value);
        this.form.get('signatureBase64').setValue(value);
        this.recomputeStepState();
    }

    private buildForm(): void {
        const sections = this.maForm.buildSections({
            // The CHD assigns the client I.D. during review, so an applicant has none to give and the
            // field is not on the page at all.
            requireClientId: false
            // No separate attestation tick. The applicant signs, and putting pen to the line is the
            // act of agreeing to the attestation printed above it, exactly as on paper and on the
            // authenticated form.
            , includeAttestation: false
            , includeGuardian: true
            // Neither how the prescription is delivered nor where it is mailed: the CHD settles both
            // with the applicant (DEPCS-4963), and the server discards anything sent for them.
            , includePrescriptionRouting: false
        });

        // Shaped to match PublicInsulinApplicationVm exactly, because RecaptchaService posts
        // formGroup.value wholesale. The form is therefore the wire contract, which is a useful
        // discipline: a control that is not on the server's allow-list has nowhere to go.
        this.form = this.fb.group({
            programType: [GenericFormType.InsulinProgramApplication]
            // The applicant's email is no longer a field beside the form: it is asked for in the
            // applicant section, which all three intake paths share, and travels inside `data`.
            , facilityId: [null, Validators.required]
            , idempotencyKey: [this.resolveIdempotencyKey()]
            , honeyPot: [null, this.mustStayEmpty]
            , recaptchaToken: [null]
            // The signature travels beside the sections, matching PublicInsulinApplicationVm. It is
            // not a form control because the pad writes an image, not a value a control would hold.
            , signatureBase64: [null]
            , data: this.fb.group({ ...sections })
        });

        this.maForm.attach(this.dataGroup, true);

        this.form.statusChanges.subscribe(() => this.recomputeStepState());
        this.form.valueChanges.subscribe(() => {
            this.recomputeStepState();
            // Any edit puts the outstanding-work summary away until Submit is pressed again.
            //
            // Gating it on the attempt alone was not enough: the panel stayed mounted afterwards and
            // went on tracking validity, so a half-typed email address still made it flash in and out
            // between "gmail." and "gmail.com". Reacting to the edit rather than to the validity means
            // it is dismissed by the first keystroke and comes back only when the applicant asks to
            // submit again - at which point it is answering a question they just asked.
            //
            // valueChanges and not statusChanges on purpose: status is what flickers, and reacting to
            // it would put the flicker straight back.
            this.submitAttempted.set(false);
        });
        this.recomputeStepState();
    }

    /**
     * A field people never see and therefore never fill in. Anything in it is automation filling in
     * every input it finds, which the server refuses as well: this only saves the round trip.
     */
    private mustStayEmpty(control: AbstractControl): ValidationErrors | null { return control.value ? { honeyPot: true } : null; }

    /**
     * One key per attempt at this form, kept in session storage so a reload does not mint a new one
     * and turn a refresh into a second application. Cleared once a submission is confirmed.
     *
     * Session storage is unavailable in some privacy modes, in which case the key is still generated
     * and still deduplicates a retry within this page load, which is the case that actually matters.
     */
    private resolveIdempotencyKey(): string {
        const storageKey = 'insulinApplicationSubmissionKey';
        try {
            const existing = sessionStorage.getItem(storageKey);
            if (existing) { return existing; }
        } catch { /* storage blocked; fall through to a fresh key */ }

        const key = this.newKey();
        try { sessionStorage.setItem(storageKey, key); } catch { /* nothing to do */ }
        return key;
    }

    private newKey(): string {
        // crypto.randomUUID is unavailable over plain http on some browsers, so fall back rather than
        // letting the whole page fail to initialise.
        try { return crypto.randomUUID().replace(/-/g, ''); } catch { /* fall through */ }
        return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
    }

    private clearIdempotencyKey(): void {
        try { sessionStorage.removeItem('insulinApplicationSubmissionKey'); } catch { /* nothing to do */ }
    }

    /**
     * Every active CHD, loaded once.
     *
     * Not keyed to the county on the application: an applicant may deal with a CHD outside the county
     * they live in, and the submission accepts any active facility. The dropdown filters, so a long
     * statewide list is still usable.
     */
    private loadFacilities(): void {
        this.http.get<MailToFacilityVm[]>('api/PublicInsulinApplication/ChdFacilities')
            .subscribe({
                next: facilities => this.servicingFacilities.set(facilities ?? [])
                , error: () => this.servicingFacilities.set([])
            });
    }

    // ---- step navigation ----
    private recomputeStepState(): void {
        const state: Record<string, boolean> = {};
        for (const s of this.steps) {
            let valid = (this.dataGroup.get(s.group) as FormGroup)?.valid ?? false;
            // The signature and the servicing CHD are asked for on the last step but do not live in
            // its group, so they are folded into that step's completeness here. The email used to be
            // folded in too; it now lives in the applicant section and is covered by that step.
            if (s.key === 'acknowledgment') {
                valid = valid
                    && !!this.signature()
                    && this.form.get('facilityId').valid;
            }
            state[s.key] = valid;
        }
        this.stepState.set(state);
    }

    isStepComplete(key: string): boolean {
        if (!this.stepState()[key]) { return false; }
        const step = this.steps.find(s => s.key === key);
        if (
            step?.optional 
            && !this.visitedSteps().has(key)
        ) { return false; }
        return true;
    }

    /** No skipping ahead past the first incomplete step. */
    canNavigateTo(key: string): boolean {
        const idx = this.steps.findIndex(s => s.key === key);
        const firstInvalid = this.firstInvalidIndex();
        return firstInvalid === -1 || idx <= firstInvalid;
    }

    goToStep(key: string): void {
        if (
            key === this.currentStep() 
            || !this.canNavigateTo(key)
        ) { return; }
        this.markVisited(this.currentStep());
        this.currentStep.set(key);
        this.focusStepHeading();
    }

    next(): void {
        if (!this.isCurrentStepValid()) {
            this.markCurrentStepTouched();
            this.recomputeStepState();
            this.ns.showError('Please complete this section before continuing.');
            this.scrollToFirstInvalid();
            return;
        }
        this.markVisited(this.steps[this.currentIndex()].key);
        const nextStep = this.steps[this.currentIndex() + 1];
        if (nextStep) {
            this.currentStep.set(nextStep.key);
            this.focusStepHeading();
        }
    }

    previous(): void {
        const prevStep = this.steps[this.currentIndex() - 1];
        if (prevStep) {
            this.currentStep.set(prevStep.key);
            this.focusStepHeading();
        }
    }

    private markVisited(key: string): void {
        if (this.visitedSteps().has(key)) { return; }
        const next = new Set(this.visitedSteps());
        next.add(key);
        this.visitedSteps.set(next);
    }

    private isCurrentStepValid(): boolean { return this.stepState()[this.steps[this.currentIndex()].key] ?? false; }

    private markCurrentStepTouched(): void {
        const step = this.steps[this.currentIndex()];
        (this.dataGroup.get(step.group) as FormGroup)?.markAllAsTouched();
        if (step.key === 'acknowledgment') {
            // Nothing to mark for the signature: the pad shows its own required state from the
            // absence of an image.
            this.form.get('facilityId').markAsTouched();
        }
    }

    /**
     * Moves focus to the step heading after a step change. Without this a keyboard or screen-reader
     * user is left at the bottom of the page with no announcement that the content changed.
     */
    private focusStepHeading(): void {
        setTimeout(() => {
            const heading = this.host.nativeElement.querySelector('.pub-step-heading') as HTMLElement | null;
            heading?.focus();
        }, 0);
    }

    /** Sends focus to the first field that is stopping the applicant, switching step if it is elsewhere. */
    private scrollToFirstInvalid(): void {
        const firstInvalid = this.firstInvalidIndex();
        const targetKey = firstInvalid >= 0 ? this.steps[firstInvalid].key : this.currentStep();
        if (targetKey !== this.currentStep()) { this.currentStep.set(targetKey); }

        setTimeout(() => {
            const content = this.host.nativeElement.querySelector('.pub-content');
            const target = content?.querySelector('.has-error, .pub-invalid') as HTMLElement | null;
            if (!target) { return; }
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const focusable = target.querySelector('input:not([type=hidden]), textarea, select') as HTMLElement | null;
            focusable?.focus({ preventScroll: true });
        }, 0);
    }

    /**
     * The steps still incomplete, for the summary shown above the actions on the last step.
     *
     * A missing signature is deliberately not one of them. It is already reported twice over - the
     * toast raised on submit, and the message under the pad itself - and the pad is on screen at the
     * moment this summary would appear, so a third line pointing at the step the applicant is already
     * looking at tells them nothing they have not just been told.
     *
     * The step is still listed when anything else in it is unanswered, because that may have scrolled
     * out of view; only the signature-alone case is dropped.
     */
    incompleteSteps(): PublicStep[] {
        const state = this.stepState();
        return this.steps.filter(s => {
            if (state[s.key]) { return false; }
            if (s.key !== 'acknowledgment') { return true; }
            // Incomplete for some reason beyond the signature? Then it still earns a line. These are
            // the same two conditions recomputeStepState folds in alongside the signature.
            return !(this.acknowledgment.valid && this.form.get('facilityId').valid);
        });
    }

    err(
        group: FormGroup
        , name: string
    ): boolean {
        const c = group?.get(name);
        return !!c && c.invalid && c.touched;
    }

    // ---- submit ----
    submit(): void {
        this.submitAttempted.set(true);
        this.form.markAllAsTouched();
        this.recomputeStepState();

        if (this.form.invalid) {
            this.ns.showError('Please complete every required field before submitting.');
            this.scrollToFirstInvalid();
            return;
        }

        this.recaptchaService.submit(
            {
                action: 'submitPublicInsulinApplication'
                , apiEndpoint: 'api/PublicInsulinApplication/SubmitApplication'
                , formGroup: this.form
                , loadingSignal: this.isSubmitting
            }
            , {
                onSuccess: (msg: SystemMessage) => {
                    if (!msg?.isSuccessful) {
                        this.ns.showError(msg?.message ?? 'We could not complete your submission. Please try again.');
                        return;
                    }
                    const result = msg.model as unknown as { confirmationNumber: string; facilityName: string };
                    this.confirmationNumber.set(result?.confirmationNumber ?? null);
                    this.facilityName.set(result?.facilityName ?? null);
                    this.submitted.set(true);
                    this.focusConfirmation();
                    // The key belongs to the attempt that just succeeded. Keeping it would make a later
                    // genuine application from the same browser return this confirmation instead.
                    this.clearIdempotencyKey();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
            }
        );
    }

    /** Puts focus on the confirmation heading so the outcome is announced and reachable. */
    private focusConfirmation(): void {
        setTimeout(() => {
            const heading = this.host.nativeElement.querySelector('.pub-confirmation-heading') as HTMLElement | null;
            heading?.focus();
        }, 0);
    }

    /**
     * Starts a clean application.
     *
     * A full reload rather than rebuilding the form in place. The shared controller wires its
     * subscriptions when a form is attached, and attaching a second one would leave the first set
     * alive against orphaned controls while carrying over state such as whether the mail-to address
     * had been hand-edited. Tearing the controller down instead is worse: its takeUntil subject would
     * be completed, so the new subscriptions would end immediately. A reload is the honest option, and
     * this is a rare action on a page with nothing worth preserving.
     *
     * The idempotency key was already cleared on success, so the fresh page mints a new one and this
     * is treated as a new application rather than a retry of the one just submitted.
     */
    startAnother(): void { window.location.reload(); }
}
